// A compact, colour-coded row of key figures for the top of manager pages: one outlined band split into cells, each
// with an accent bar, a label, the value, an optional hint, progress line or action. Wraps on narrow screens.

import React from "react";
import { Box, LinearProgress, Paper, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

export type StatColor = "primary" | "success" | "info" | "warning" | "error" | "secondary";

export interface StatItem {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  color?: StatColor;
  /** 0-100: a thin progress line under the value. */
  progress?: number;
  /** A small button or link under the value. */
  action?: React.ReactNode;
  onClick?: () => void;
}

export const StatStrip: React.FC<{ items: StatItem[]; minCell?: number }> = ({ items, minCell = 170 }) => {
  const theme = useTheme();
  return (
    <Paper variant="outlined" sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr 1fr", md: `repeat(auto-fit, minmax(${minCell}px, 1fr))` },
      overflow: "hidden",
      mb: 2,
    }}>
      {items.map((item, index) => {
        const color = theme.palette[item.color ?? "primary"].main;
        return (
          <Box
            key={item.label}
            onClick={item.onClick}
            sx={{
              position: "relative",
              px: 2,
              py: 1.25,
              borderLeft: index ? 1 : 0,
              borderTop: { xs: index > 1 ? 1 : 0, md: 0 },
              borderColor: "divider",
              cursor: item.onClick ? "pointer" : "default",
              background: `linear-gradient(90deg, ${alpha(color, 0.07)}, transparent 70%)`,
              "&::before": {
                content: '""', position: "absolute", left: 0, top: 10, bottom: 10, width: 3, borderRadius: 2, bgcolor: color,
              },
              "&:hover": item.onClick ? { bgcolor: alpha(color, 0.05) } : undefined,
            }}
          >
            <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: "uppercase", color }} noWrap>
              {item.label}
            </Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.3, fontVariantNumeric: "tabular-nums" }} noWrap>
              {item.value}
            </Typography>
            {item.hint && <Typography variant="caption" color="text.secondary" component="div" noWrap>{item.hint}</Typography>}
            {item.progress !== undefined && (
              <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, item.progress))}
                sx={{ mt: 0.75, height: 3, borderRadius: 2, bgcolor: alpha(color, 0.15), "& .MuiLinearProgress-bar": { bgcolor: color } }} />
            )}
            {item.action && <Box sx={{ mt: 0.75 }}>{item.action}</Box>}
          </Box>
        );
      })}
    </Paper>
  );
};
